/* Santa Cart Sync — robust auto-correct during cart edits */
(function () {
  const CFG = {
    adultHandles: ["adult-ticket-tea", "adult-ticket-breakfast"],
    childHandles: ["child-ticket-tea", "child-ticket-breakfast"],
    masterProps: ["Adult Tickets", "Child Tickets", "Total Party Size"],
    errorContainerSelector: '[data-santa-cart-errors], .cart__footer, form[action="/cart"]',
    debounceMs: 250
  };

  let lastSnapshot = null;
  let syncBusy = false;
  let debounceTimer = null;

  const dlog = (...args) => { if (window.SANTA_SYNC_DEBUG) console.debug("[Santa Sync]", ...args); };

  function onReady(fn){ if (document.readyState !== "loading") fn(); else document.addEventListener("DOMContentLoaded", fn); }
  function sleep(ms){ return new Promise(r => setTimeout(r, ms)); }

  async function getCart() {
    const res = await fetch("/cart.js", { headers: { "Accept": "application/json" }, credentials: "same-origin" });
    if (!res.ok) throw new Error("Failed to load cart");
    return res.json();
  }

  // Prefer changing by key, but fall back to line index if key is missing
  async function changeItem(cart, item, payload) {
    const body = {};
    if (item && item.key) {
      body.id = item.key; // line item key
    } else {
      const idx = (cart.items || []).findIndex(it => it === item);
      if (idx >= 0) body.line = idx + 1; // 1-based line number
    }
    Object.assign(body, payload);

    const res = await fetch("/cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", "Accept": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify(body)
    });
    if (!res.ok) throw new Error(await res.text().catch(() => "change failed"));
    return res.json();
  }

  function handleOf(item){ return (item && item.handle) || ""; }
  function hasMasterProps(item){
    const p = item && item.properties;
    if (!p) return false;
    return CFG.masterProps.every(k => Object.prototype.hasOwnProperty.call(p, k));
  }

  function classify(cart){
    const groups = { master: null, adults: [], children: [], other: [] };
    (cart.items || []).forEach(it => {
      if (!groups.master && hasMasterProps(it)) groups.master = it;
      else if (CFG.adultHandles.includes(handleOf(it))) groups.adults.push(it);
      else if (CFG.childHandles.includes(handleOf(it))) groups.children.push(it);
      else groups.other.push(it);
    });
    return groups;
  }

  function counts(groups){
    const adultQty = groups.adults.reduce((s, it) => s + (it.quantity || 0), 0);
    const childQtySum = groups.children.reduce((s, it) => s + (it.quantity || 0), 0);
    const childLines = groups.children.length;
    return { adultQty, childQtySum, childLines, partySize: adultQty + childQtySum };
  }

  function i(x){ const n = parseInt((x ?? "0"), 10); return Number.isFinite(n) ? n : 0; }
  function readMasterNumbers(master){
    const p = (master && master.properties) || {};
    return { mpAdults: i(p["Adult Tickets"]), mpChildren: i(p["Child Tickets"]), mpParty: i(p["Total Party Size"]) };
  }

  function renderError(msg){
    let target = document.querySelector(CFG.errorContainerSelector);
    if (target && !target.matches('[data-santa-cart-errors]')) {
      const block = document.createElement("div");
      block.setAttribute("data-santa-cart-errors", "true");
      target.parentNode.insertBefore(block, target);
      target = block;
    }
    if (!target) target = document.body;

    // Clear previous from this sync
    const old = target.querySelector('[data-santa-sync-error]');
    if (old) old.remove();

    const box = document.createElement("div");
    box.setAttribute("data-santa-sync-error", "true");
    box.setAttribute("role", "alert");
    box.style.border = "1px solid #e00";
    box.style.background = "#fff5f5";
    box.style.color = "#b00020";
    box.style.padding = "12px";
    box.style.borderRadius = "10px";
    box.style.margin = "12px 0";
    box.textContent = msg;

    target.prepend(box);
    box.scrollIntoView({ behavior: "smooth", block: "center" });
  }

  function detectAttemptedChildAdd(prevCart, curCart, master) {
    if (!prevCart) return false;
    const prev = counts(classify(prevCart));
    const cur  = counts(classify(curCart));
    const { mpChildren } = readMasterNumbers(master);
    // Increase in child unit count or child lines, or exceeding master’s recorded count
    return (cur.childQtySum > prev.childQtySum) || (cur.childLines > prev.childLines) || (cur.childQtySum > mpChildren);
  }

  async function syncCartNow(reason = "unknown") {
    if (syncBusy) return;
    syncBusy = true;
    try {
      dlog("sync start:", reason);
      let cart = await getCart();
      let g = classify(cart);

      // A) If master removed → remove all tickets
      if (!g.master) {
        dlog("no master; removing all adult/child tickets");
        for (const it of [...g.adults, ...g.children]) {
          await changeItem(cart, it, { quantity: 0 });
        }
        lastSnapshot = await getCart();
        dlog("sync done (master removed)");
        return;
      }

      // B) Force each child line to qty=1
      for (const it of g.children) {
        if ((it.quantity || 0) !== 1) {
          dlog("forcing child qty=1", it);
          await changeItem(cart, it, { quantity: 1 });
          cart = await getCart();
          g = classify(cart);
        }
      }

      // C) Align master properties with actual counts (adults changed, child removed, etc.)
      let c = counts(g);
      const mNums = readMasterNumbers(g.master);
      const needUpdate = (mNums.mpAdults !== c.adultQty) || (mNums.mpChildren !== c.childQtySum) || (mNums.mpParty !== c.partySize);

      if (needUpdate) {
        const newProps = Object.assign({}, g.master.properties || {}, {
          "Adult Tickets": String(c.adultQty),
          "Child Tickets": String(c.childQtySum),
          "Total Party Size": String(c.partySize)
        });
        dlog("updating master props", newProps);
        await changeItem(cart, g.master, { 
          quantity: c.partySize, 
          properties: newProps 
        });
        cart = await getCart();
        g = classify(cart);
        c = counts(g);
      }

      // D) Block adding children from cart (must be added via event page for details)
      const attemptedAddChild = detectAttemptedChildAdd(lastSnapshot, cart, g.master);
      if (attemptedAddChild) {
        dlog("detected attempted child add; reverting");
        const { mpChildren } = readMasterNumbers(g.master);
        const allowed = mpChildren > 0 ? mpChildren :
                        (lastSnapshot ? counts(classify(lastSnapshot)).childQtySum : 0);

        // Remove extras by trimming from the end (likely newest)
        let childList = [...g.children];
        let curChildUnits = childList.reduce((s, it) => s + (it.quantity || 0), 0);
        while (curChildUnits > allowed && childList.length) {
          const victim = childList.pop();
          await changeItem(cart, victim, { quantity: 0 });
          curChildUnits -= (victim.quantity || 0);
        }

        renderError("To add another child, please return to the event page so we can collect their details.");
        cart = await getCart();
      }

      lastSnapshot = cart;
      dlog("sync done");
    } catch (e) {
      console.warn("[Santa Sync] Error:", e);
    } finally {
      syncBusy = false;
    }
  }

  function scheduleSync(reason){ clearTimeout(debounceTimer); debounceTimer = setTimeout(() => syncCartNow(reason), CFG.debounceMs); }

  function hookCartUI() {
    // 1) Any input/change inside the cart form triggers a sync
    document.addEventListener("input",  (e) => { if (e.target.closest('form[action="/cart"]')) scheduleSync("form-input"); },  true);
    document.addEventListener("change", (e) => { if (e.target.closest('form[action="/cart"]')) scheduleSync("form-change"); }, true);

    // 2) Common controls: plus/minus, remove buttons, drawer buttons
    document.addEventListener("click", (e) => {
      if (e.target.closest('[data-cart-remove], .cart__remove, .quantity__button, [name="updates[]"], [name^="updates["], [data-ajax-cart], [data-update-cart]')) {
        scheduleSync("click-mutate");
      }
    }, true);

    // 3) Intercept cart UPDATE submits (not just checkout) and sync first
    const cartForm = document.querySelector('form[action="/cart"]');
    if (cartForm) {
      cartForm.addEventListener("submit", (e) => {
        const submitter = e.submitter || document.activeElement;
        const isCheckout = submitter && ((submitter.name === "checkout") || /checkout/i.test(submitter.value || ""));
        if (!isCheckout) {
          e.preventDefault();
          syncCartNow("cart-submit-update").then(() => cartForm.submit());
        }
      }, true);
    }

    // 4) Observe AJAX changes: hook fetch for /cart/change.js and /cart/update.js
    const _fetch = window.fetch;
    window.fetch = async function (input, init) {
      const p = _fetch.apply(this, arguments);
      try {
        const url = (typeof input === "string" ? input : (input && input.url)) || "";
        const method = (init && (init.method || "GET")).toUpperCase();
        const touchesCart = /\/cart\/(change|update)\.js(\?|$)/.test(url) && (method === "POST");
        const res = await p;
        if (touchesCart) {
          await sleep(100);
          scheduleSync("ajax-change");
        }
        return res;
      } catch (_) {
        return p;
      }
    };

    // 5) MutationObserver: themes/apps often re-render cart DOM (e.g., drawers)
    const observer = new MutationObserver((mut) => {
      // Any substantial child list change likely means cart mutated
      const significant = mut.some(m => (m.type === "childList" && (m.addedNodes.length || m.removedNodes.length)));
      if (significant) scheduleSync("dom-mutation");
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  onReady(async () => {
    hookCartUI();
    try { lastSnapshot = await getCart(); } catch (_) {}
    scheduleSync("init");
  });
})();
